import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useApi } from './useApi';

export type ReminderColumnKey = 'da_fare' | 'in_corso' | 'fatto';
export type Priority = 'alta' | 'media' | 'bassa' | null;

export interface ReminderCard {
  id: string;
  ical_uid: string;
  title: string | null;
  description: string | null;
  due: string | null;
  priority: Priority;
  completed: boolean;
  column: ReminderColumnKey;
  calendar_name: string | null;
}

export interface ReminderColumn {
  key: ReminderColumnKey;
  title: string;
  cards: ReminderCard[];
}

export interface ReminderMeta {
  configured: boolean;
  syncEnabled: boolean;
  lastSync: { started_at?: string; success?: boolean; error_message?: string | null } | null;
}

const ORDER: ReminderColumnKey[] = ['da_fare', 'in_corso', 'fatto'];
const TITLES: Record<ReminderColumnKey, string> = {
  da_fare: 'Da fare',
  in_corso: 'In corso',
  fatto: 'Fatto',
};

function rebucket(cards: ReminderCard[]): ReminderColumn[] {
  return ORDER.map((key) => ({
    key,
    title: TITLES[key],
    cards: cards.filter((c) => c.column === key),
  }));
}

export function useReminders() {
  const api = useApi();
  const [columns, setColumns] = useState<ReminderColumn[]>(rebucket([]));
  const [meta, setMeta] = useState<ReminderMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api('/reminders');
      setColumns(data.columns);
      setMeta(data.meta);
      setError(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const allCards = () => columns.flatMap((c) => c.cards);

  const createReminder = useCallback(
    async (fields: { title: string; description?: string; due?: string | null; priority?: Priority }) => {
      try {
        await api('/reminders', { method: 'POST', body: JSON.stringify(fields) });
        toast.success('Promemoria creato');
        await load();
      } catch (e: any) {
        toast.error(e.message || 'Creazione fallita');
        throw e;
      }
    },
    [api, load]
  );

  const updateReminder = useCallback(
    async (id: string, fields: { title?: string; description?: string | null; due?: string | null; priority?: Priority }) => {
      try {
        const card: ReminderCard = await api(`/reminders/${id}`, { method: 'PATCH', body: JSON.stringify(fields) });
        setColumns((prev) => rebucket(prev.flatMap((c) => c.cards).map((c) => (c.id === id ? card : c))));
        toast.success('Promemoria aggiornato');
      } catch (e: any) {
        toast.error(e.message || 'Aggiornamento fallito');
        await load();
        throw e;
      }
    },
    [api, load]
  );

  const moveReminder = useCallback(
    async (id: string, toColumn: ReminderColumnKey) => {
      const snapshot = columns;
      // Optimistic: ri-bucketizza la card nella nuova colonna.
      const moved = allCards().map((c) =>
        c.id === id ? { ...c, column: toColumn, completed: toColumn === 'fatto' } : c
      );
      setColumns(rebucket(moved));
      try {
        await api(`/reminders/${id}/move`, { method: 'POST', body: JSON.stringify({ column: toColumn }) });
      } catch (e: any) {
        toast.error(e.message || 'Spostamento fallito');
        setColumns(snapshot);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, columns]
  );

  const deleteReminder = useCallback(
    async (id: string) => {
      const snapshot = columns;
      setColumns(rebucket(allCards().filter((c) => c.id !== id)));
      try {
        await api(`/reminders/${id}`, { method: 'DELETE' });
        toast.success('Promemoria eliminato');
      } catch (e: any) {
        toast.error(e.message || 'Eliminazione fallita');
        setColumns(snapshot);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, columns]
  );

  return { columns, meta, loading, error, reload: load, createReminder, updateReminder, moveReminder, deleteReminder };
}
